import { useRef } from 'react';
import axios from 'axios';
import { ApiRequest } from '../types';

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

const { REACT_APP_API_BASE_URL = 'http://localhost:8000' } = process.env;

type Options = {
  throwIfError: boolean;
};

/**
 * Carries the HTTP status alongside the server's `detail`, so callers can tell
 * a rate limit from any other failure. `message` is unchanged, so consumers
 * that only read it keep working.
 */
export class ApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export const HTTP_TOO_MANY_REQUESTS = 429;

export const useApi = ({ throwIfError }: Options = { throwIfError: false }) => {
  const controllerRef = useRef(new AbortController());

  const cancel = () => {
    controllerRef.current.abort();
  };

  const api = async <PayloadType = undefined>(
    request: ApiRequest<PayloadType>
  ) => {
    const requestHeaders = request.headers ?? DEFAULT_HEADERS;
    const headers = request.token
      ? { ...requestHeaders, Authorization: `Bearer ${request.token.trim()}` }
      : requestHeaders;

    try {
      const requestData = {
        data: request.payload,
        headers,
        method: request.method || 'GET',
        signal: controllerRef.current.signal,
        url:
          request.url ||
          [REACT_APP_API_BASE_URL, request.route.replace(/^\/+/, '')].join('/'),
      };

      const response = await axios.request(requestData);
      return response.data;
    } catch (error: any) {
      console.error(error?.message || 'Unknown error');

      if (throwIfError) {
        throw new ApiError(
          error?.response?.data?.detail ?? 'Error with unknown details.',
          error?.response?.status
        );
      }
    }

    return null;
  };

  return { api, cancel };
};
